import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Building2, FileText, ShieldCheck, Phone, Mail,
  Clock3, Users, MapPin,
} from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { FAQ } from "@/components/FAQ";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";
import {
  SchemaJsonLd,
  serviceSchema,
  faqSchema,
  localBusinessSchema,
} from "@/lib/seo";
import { CONTACT } from "@/lib/siteData";

const FAQS = [
  {
    q: "Do you work with real estate agencies and property managers?",
    a: "Yes — Aware Water Testing regularly works with property managers, real estate agencies, and conveyancers managing rental, sales, and tenancy water-quality requests on tank water and bore water supplies. Contact the lab for volume pricing and reporting options.",
  },
  {
    q: "Can you invoice the agency directly?",
    a: "Yes. We can invoice your agency directly for tests booked on behalf of landlords or vendors — speak to us about setting up a trade account.",
  },
  {
    q: "How fast can you turn around a test for a property settlement?",
    a: "Reports are usually returned within 1–3 days, depending on the test package, sample timing and lab workload. If you have a settlement deadline, contact the lab in advance and we'll do everything we can to fit the timeline.",
  },
  {
    q: "Do you do town water testing for compliance?",
    a: "Aware focuses on tank water and bore water testing. We don't currently test town water supplies — town water is already tested by your local council provider.",
  },
];

export default function PropertyManagersPage() {
  const url = `${SEO_DEFAULTS.baseUrl}/property-managers`;
  useSEO({
    title: "Property Managers & Real Estate | Tank & Bore Water Testing Australia-wide",
    description:
      "Tank and bore water testing for property managers, real estate agencies and conveyancers Australia-wide. Plain-English reports, agency invoicing, fast settlement turnaround. Local drop-off available in Noosaville, plus posted sample options nationally.",
    url,
  });

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SchemaJsonLd data={localBusinessSchema()} id="jsonld-localbusiness" />
      <SchemaJsonLd
        data={serviceSchema(
          "Water Testing for Property Managers",
          "Tank and bore water testing services for property managers, real estate agencies and conveyancers across Australia.",
          "Australia"
        )}
        id="jsonld-service"
      />
      <SchemaJsonLd data={faqSchema(FAQS)} id="jsonld-faq" />

      <Header />

      <main>
        {/* HERO */}
        <section className="pt-28 lg:pt-36 pb-12 lg:pb-16 bg-gradient-to-br from-slate-50 via-white to-cyan-50/40 border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Breadcrumbs items={[{ name: "Property Managers" }]} />

            <div className="mt-6 max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm mb-5">
                <Building2 className="w-4 h-4 text-cyan-600" />
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700">
                  For agencies &amp; property professionals
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-tight">
                Water testing for property managers &amp; real estate
              </h1>

              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                Aware Water Testing supports property managers, real estate agencies, and conveyancers with reliable, easy-to-read tank water and bore water analysis. Whether it's a tenancy request, a pre-settlement check, or a routine annual test — we make the process simple.
              </p>

              <p className="mt-3 text-sm text-slate-500">
                Manage water testing records across your rural rental
                portfolio with an AWARE Water Health Record for each
                property.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to="/property-managers/apply"
                  data-testid="pm-apply-hero-cta"
                  className="btn-aqua inline-flex items-center gap-1.5 px-6 py-3 rounded-full text-sm font-semibold"
                >
                  Apply for an agency account <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/book-water-test"
                  data-testid="pm-book-btn"
                  className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full text-sm font-semibold border border-slate-200 hover:border-cyan-400 hover:text-cyan-700 transition-colors"
                >
                  Book a one-off property test
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* BENEFITS */}
        <section className="py-14 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: FileText, title: "Plain-English reports", desc: "Easy-to-share PDF reports your landlords, tenants and clients can actually understand." },
                { icon: ShieldCheck, title: "Recognised methodologies", desc: "Local water testing performed using documented laboratory procedures." },
                { icon: Clock3, title: "Settlement-friendly turnaround", desc: "Reports usually in 1–3 days, with priority handling on tight settlements when needed." },
                { icon: Users, title: "Agency invoicing", desc: "Trade accounts available — invoice your agency directly instead of billing per tenant." },
              ].map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.title} className="rounded-2xl bg-white border border-slate-200 p-6 hover:border-cyan-300 hover:shadow-lg transition-all">
                    <div className="w-10 h-10 rounded-xl bg-cyan-50 text-cyan-700 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="font-display text-lg font-semibold mb-2">{b.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* TESTS FOR PROPERTIES */}
        <section className="py-14 lg:py-20 bg-slate-50/60 border-y border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mb-10">
              <h2 className="text-3xl font-display font-bold mb-3">
                Recommended tests for managed properties
              </h2>
              <p className="text-base text-slate-600 leading-relaxed">
                Most agency requests are for one of the three core test packages below. If you're not sure which is appropriate for your property, contact the lab — we'll guide you.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              {[
                { to: "/aware-4-point", name: "AWARE 4-Point", desc: "Quick screening for E. coli, coliforms, pH and conductivity. Ideal for fast tenancy checks." },
                { to: "/aware-tank-360", name: "AWARE Tank 360", desc: "Comprehensive rainwater tank package. Recommended for properties on tank-only supply." },
                { to: "/aware-bore-360", name: "AWARE Bore 360", desc: "Built for bore water properties — drinking, domestic, irrigation or livestock use." },
              ].map((t) => (
                <Link
                  key={t.to}
                  to={t.to}
                  className="group block rounded-2xl bg-white border border-slate-200 p-6 hover:border-cyan-300 hover:shadow-lg transition-all"
                >
                  <h3 className="font-display text-lg font-semibold mb-2">{t.name}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4">{t.desc}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-cyan-700 group-hover:gap-2 transition-all">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CONTACT BAND */}
        <section className="py-14 lg:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl bg-slate-900 text-white p-8 lg:p-12">
              <div className="grid lg:grid-cols-2 gap-8 items-start">
                <div>
                  <h2 className="text-3xl font-display font-bold mb-3">Set up an agency account</h2>
                  <p className="text-slate-300 leading-relaxed">
                    Apply to set up a property manager account and start
                    building water testing history across the properties
                    you manage. Once approved you'll get a Water Health
                    AWARE Water Health Record dashboard for every assigned property.
                  </p>
                  <Link
                    to="/property-managers/apply"
                    data-testid="pm-apply-footer-cta"
                    className="mt-5 inline-flex items-center gap-1.5 px-6 py-3 rounded-full text-sm font-semibold bg-cyan-500 hover:bg-cyan-400 text-slate-900 transition-colors"
                  >
                    Apply for an agency account <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className="space-y-3">
                  <a
                    href={CONTACT.phoneLink}
                    data-testid="pm-phone"
                    className="flex items-center gap-3 rounded-xl bg-white/5 hover:bg-white/10 px-5 py-4 transition-colors"
                  >
                    <Phone className="w-5 h-5 text-cyan-400" />
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-400">Phone</div>
                      <div className="font-semibold">{CONTACT.phone}</div>
                    </div>
                  </a>
                  <a
                    href={`mailto:${CONTACT.email}`}
                    data-testid="pm-email"
                    className="flex items-center gap-3 rounded-xl bg-white/5 hover:bg-white/10 px-5 py-4 transition-colors"
                  >
                    <Mail className="w-5 h-5 text-cyan-400" />
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-400">Email</div>
                      <div className="font-semibold break-all">{CONTACT.email}</div>
                    </div>
                  </a>
                  <div className="flex items-center gap-3 rounded-xl bg-white/5 px-5 py-4">
                    <MapPin className="w-5 h-5 text-cyan-400" />
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-400">Lab</div>
                      <div className="font-semibold">{CONTACT.address}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQ qas={FAQS} subtitle="FAQ" title="For property managers" testIdPrefix="pm-faq" />
      </main>

      <Footer />
    </div>
  );
}
